import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  leavePanelClassName,
  leaveTableFrameClassName,
} from '@/products/leave/features/leave-panel';
import {
  useCreateCompanyHoliday,
  useDeleteLeaveHoliday,
  useGenerateLeaveHolidays,
  useLeaveHolidays,
} from '@/products/leave/features/use-leave-admin';

export function LeaveAdminHolidaysPage() {
  usePageTitle('Feiertage');
  const [year, setYear] = useState(new Date().getFullYear());
  const holidays = useLeaveHolidays(year);
  const generate = useGenerateLeaveHolidays();
  const create = useCreateCompanyHoliday();
  const remove = useDeleteLeaveHoliday();
  const [date, setDate] = useState('');
  const [nameDe, setNameDe] = useState('');
  const [errors, setErrors] = useState<{ date?: string; nameDe?: string }>({});

  async function onGenerate() {
    try {
      const result = await generate.mutateAsync(year);
      toast.success(`${result.created} Feiertage für ${year} angelegt.`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Feiertage konnten nicht erzeugt werden.',
      );
    }
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    const nextErrors: { date?: string; nameDe?: string } = {};
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      nextErrors.date = 'Bitte ein Datum im Format JJJJ-MM-TT eingeben.';
    }
    if (!nameDe.trim()) {
      nextErrors.nameDe = 'Bitte einen Namen eingeben.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    try {
      await create.mutateAsync({ date, nameDe: nameDe.trim() });
      setDate('');
      setNameDe('');
      toast.success('Firmenfeiertag gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Firmenfeiertag konnte nicht gespeichert werden.',
      );
    }
  }

  async function onDelete(id: string) {
    try {
      await remove.mutateAsync({ id, year });
      toast.success('Feiertag entfernt.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Feiertag konnte nicht entfernt werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Feiertage"
        description="Gesetzliche Feiertage erzeugen und Firmenfeiertage pflegen."
      />
      <div className={`flex flex-wrap items-end gap-3 ${leavePanelClassName}`}>
        <div>
          <Label htmlFor="holiday-year">Jahr</Label>
          <Input
            id="holiday-year"
            type="number"
            className="mt-2 w-32"
            value={year}
            onChange={(event) => setYear(Number(event.target.value))}
          />
        </div>
        <Button type="button" disabled={generate.isPending} onClick={() => void onGenerate()}>
          Gesetzliche Feiertage erzeugen
        </Button>
      </div>
      <form
        className={`${leavePanelClassName} space-y-4`}
        onSubmit={(event) => void onCreate(event)}
      >
        <h2 className="text-lg font-semibold">Firmenfeiertag anlegen</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="company-date">Datum</Label>
            <Input
              id="company-date"
              type="date"
              className="mt-2"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            {errors.date ? <p className="mt-1 text-sm text-destructive">{errors.date}</p> : null}
          </div>
          <div>
            <Label htmlFor="company-name">Bezeichnung</Label>
            <Input
              id="company-name"
              className="mt-2"
              value={nameDe}
              onChange={(event) => setNameDe(event.target.value)}
            />
            {errors.nameDe ? (
              <p className="mt-1 text-sm text-destructive">{errors.nameDe}</p>
            ) : null}
          </div>
        </div>
        <Button type="submit" disabled={create.isPending}>
          Speichern
        </Button>
      </form>
      {holidays.isPending ? (
        <p className="text-sm text-muted-foreground">Feiertage werden geladen …</p>
      ) : null}
      {holidays.data?.items.length ? (
        <div className={leaveTableFrameClassName}>
          <table className="min-w-full text-sm">
            <thead className="border-b bg-muted/40 text-left">
              <tr>
                <th className="px-3 py-2">Datum</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Art</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {holidays.data.items.map((item) => (
                <tr key={item.id} className="border-b last:border-b-0">
                  <td className="px-3 py-2">{formatDate(item.date)}</td>
                  <td className="px-3 py-2">{item.nameDe}</td>
                  <td className="px-3 py-2">
                    {item.scope === 'company' ? 'Firma' : `Bundesland ${item.stateCode ?? ''}`}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={remove.isPending}
                      onClick={() => void onDelete(item.id)}
                    >
                      Entfernen
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Für dieses Jahr sind noch keine Feiertage hinterlegt.
        </p>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('de-DE').format(new Date(`${iso}T12:00:00`));
}
